import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiClientError } from '../lib/api';
import { useSession } from '../lib/session';
import { Banner, Button, Card, CardBody, CardHeader, Field, Input, Spinner } from '../components/ui';

/**
 * Change-password screen, used for two flows:
 *   - Forced first login / admin reset (user.mustChangePassword): the user signs
 *     in with their temporary password and must pick a new one before reaching
 *     the rest of the app.
 *   - Voluntary change from the account link.
 *
 * The server enforces the password policy and returns a precise message listing
 * any unmet requirements, so we surface that directly rather than duplicating the
 * (admin-only) policy here.
 */
export function ChangePassword() {
  const { user, changePassword } = useSession();
  const navigate = useNavigate();
  const forced = !!user?.mustChangePassword;

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (newPassword !== confirm) {
      setError('New password and confirmation do not match.');
      return;
    }
    setBusy(true);
    try {
      await changePassword(currentPassword, newPassword);
      navigate('/');
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not change your password.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-md">
      <Card>
        <CardHeader
          title={forced ? 'Set a new password' : 'Change password'}
          subtitle={
            forced
              ? 'Your password was issued temporarily — choose a new one to continue.'
              : 'Update the password for your account.'
          }
        />
        <CardBody>
          <form onSubmit={onSubmit} className="space-y-4">
            {error && <Banner tone="error">{error}</Banner>}
            {forced && (
              <Banner tone="warn">
                You must set your own password before you can use the rest of the app.
              </Banner>
            )}
            <Field label={forced ? 'Temporary password' : 'Current password'}>
              <Input
                type="password"
                required
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                autoComplete="current-password"
              />
            </Field>
            <Field label="New password" hint="Must meet your team's password policy.">
              <Input
                type="password"
                required
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
              />
            </Field>
            <Field label="Confirm new password">
              <Input
                type="password"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
              />
            </Field>
            <div className="flex items-center gap-2">
              <Button type="submit" disabled={busy}>
                {busy ? <Spinner /> : 'Update password'}
              </Button>
              {!forced && (
                <Button type="button" variant="ghost" onClick={() => navigate('/')} disabled={busy}>
                  Cancel
                </Button>
              )}
            </div>
          </form>
        </CardBody>
      </Card>
    </div>
  );
}
